/**
 * `<DragHandle>` — the grip button that owns a sortable row's drag interaction.
 *
 * Wraps the `attributes` + `listeners` from dnd-kit's `useSortable` in a button
 * with the app-standard grip icon, `cursor-grab`, and the a11y focus-ring
 * (`focus-visible:ring-*`) that keyboard-drag depends on. Extracted from the
 * verbatim copies in `ApprovalChainStepRow` and `BriefSectionRow` so a
 * single edit reaches every sortable surface.
 *
 * Pair with `useSortableList` (`@/shared/hooks`) for the sensors + drag-end
 * plumbing.
 */
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import clsx from 'clsx';
import { GripVerticalIcon } from 'lucide-react';

export interface DragHandleProps {
  /** Describes what this handle reorders, e.g. `Reorder step 1`. */
  ariaLabel: string;
  /** `attributes` from `useSortable` — ARIA + keyboard wiring. */
  attributes: DraggableAttributes;
  /** `listeners` from `useSortable` — pointer/keyboard drag start. */
  listeners: DraggableSyntheticListeners;
  /**
   * Hover tooltip. Worth setting where `ariaLabel` names the specific row it
   * reorders: sighted users get the generic "what this does" the per-row label
   * cannot double as.
   */
  title?: string;
  /** Disables the handle (e.g. while the form is submitting). */
  disabled?: boolean;
  /**
   * Needed when the handle sits inside a row that is itself clickable: without
   * `e.stopPropagation()` here, grabbing the grip also activates the row.
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Extra classes merged onto the button (rarely needed). */
  className?: string;
}

export function DragHandle(props: DragHandleProps) {
  const { ariaLabel, attributes, listeners, title, disabled, onClick, className } = props;
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      title={title}
      className={clsx(
        'cursor-grab rounded text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed',
        className,
      )}
      disabled={disabled}
      onClick={onClick}
      {...attributes}
      {...listeners}
    >
      <GripVerticalIcon aria-hidden className="size-4" />
    </button>
  );
}

DragHandle.displayName = 'DragHandle';
